import { ReplaySubject } from 'rxjs';

import AlertDialog from '../../components/ui/dialogs/alert-dialog.component';
import { container } from '../../context/module';
import { DropboxFile } from '../../models/dropbox-file';
import { FileUploadRequestMetadata } from '../../models/file-upload-request-metadata';
import { GoogleDriveFile } from '../../models/google-drive-file';
import { Logger } from '../../models/logger';
import { UploadInProgressFile, UploadInProgressFileFactory, isUploadCancelled } from '../../models/upload-in-progress-file';
import { GAService } from '../../services/analytics.service';
import { ApiConfig } from '../../services/api-config';
import { EnforcementErrorService } from '../../services/enforcement-error.service';
import { FileUploadService } from '../../services/file-upload.service';
import { LightHttp } from '../../services/light-http.service';
import { ProcessorResolverService } from '../../services/processor-resolver.service';
import { UIContext } from '../../services/ui-context';
import { FileSelectEvent, FileSelectSource, UploadOptions } from '../../types/file-upload.types';
import { GAContants } from '../../types/ga.types';
import { Observer } from 'rxjs';
import { OperationTypes, OriginalInputFile } from '../../types/types';
import { ConnectivityUtils } from '../../utils/connectivity.utils';
import { filter } from '../../utils/lodash-min';
import { safeExt, sizeBucket } from '../../utils/metric-attrs';
import { StringUtils } from '../../utils/string-utils';
import { FileValidationService, RejectedFile } from '../../services/file-validation.service';
import { FeatureRedirectService } from '../../services/feature-redirect.service';
import { MetricsService } from '../../services/metrics.service';
import { MetricEventName } from '../../types/metric-events';
import { RouteUtils } from '../../utils/route.utils';
import { everyVideoExtension } from '../frame-picker/files-pane/pick-files';
// The ONE edge this file takes into `components/studio/` and the only one D10 permits: a leaf
// module that imports nothing at all, touches no DOM at module scope and resolves to four byte
// sniffers. Verified at source 2026-09-17 -- `components/studio/media/animated.ts` has no import
// statement of any kind, so this adds nothing to the static upload graph but the sniffers
// themselves. The container edge that crashes a webpack module eval
// (`file-convert.component.tsx:813-822`) is exactly what is NOT here.
import { isAnimatedImage } from '../studio/media/animated';
// The second, and for the same reason: `components/studio/studio-gate.ts` imports NOTHING at all
// (verified at source 2026-09-17), so this costs the static upload graph one predicate and no
// edge. It is here because the nudge's byte sniff must not run on the ~500 converter pages that
// would throw the answer away.
import { pageAcceptsStudio } from '../studio/studio-gate';


export type SupportedFileTypes = DropboxFile | GoogleDriveFile | File;

/**
 * How one caller wants the uploader to behave — STUDIO_DESIGN.md §6.2.
 *
 * Every field exists for the Studio, which drives this same manager from a tab whose rules differ
 * from the page's: it takes clips and music as well as photos, and it reports every outcome in
 * its own tray rather than through the site's dialogs and toasts.
 *
 * The whole interface is optional and the parameter that carries it is optional, so a call site
 * that passes nothing behaves exactly as it does today — which is the contract the two shipped
 * call sites (`file-convert.component.tsx:505`, `shared/av-base.component.tsx:220`) rely on and
 * `file-upload-manager.policy.spec.ts` asserts by identity on the page-data object.
 */
export interface UploadPolicy {
    /**
     * Replaces the page's accept list for validation ONLY.
     *
     * `pageData.path` is untouched, so `pageSlug` on INVALID_FILE_SELECTED and
     * VALIDATION_BANNER_SHOWN stays the real route (§6.2). A Studio pick that is rejected is
     * still a rejection ON THAT PAGE, and a synthetic slug would make the funnel unjoinable.
     */
    acceptedExtensions?: string[];
    /**
     * Suppress the five user-visible side effects and route them through `validationResult`
     * instead: the offline dialog, the "Preparing To Upload" toast, the size-limit alert, the
     * "Server Unreachable" dialog and the generic error dialog.
     */
    quiet?: boolean;
    /**
     * Default true. The Studio's own uploader sets it false so the editor cannot nudge itself.
     *
     * A THUNK when the answer is not known yet. This manager is constructed ONCE, on the first
     * pick (`file-convert.component.tsx:528`), and the converter page's verdict on the Studio
     * arrives later, from an api-sourced `featureFlags.studio` emission the page base subscribes
     * to — so a captured boolean would freeze the answer from before the API spoke. With §11.1
     * step 3's dark write in place (`studio:false` on prod BEFORE the web deploy), that stale
     * answer is "sniff": `maybeNudge` reads up to `ANIMATED_SNIFF_BYTES` (1 MB) per file,
     * sequentially, alongside the upload the user is waiting on, on each of the 37 image→video
     * pages — and the gate downstream throws the answer away (final review I-2).
     */
    nudge?: boolean | (() => boolean);
}

/** What `stillImageNudge` carries: how many still images were accepted in this pick. */
export interface StillImageNudge {
    files: number;
}

export interface FileSelectHandleRequest<T> {
    files: T[];
    fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[];
    fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[];
}

export class FileUploadManager {
    private fileUploadService: FileUploadService;
    private processorResolverService: ProcessorResolverService;
    private uiContext: UIContext;
    private gaService: GAService;

    private apiConfig: ApiConfig;
    private lightHttp: LightHttp;
    private fileValidationService: FileValidationService;
    private featureRedirectService: FeatureRedirectService;
    private metricsService: MetricsService;

    /** `cause` (round 6, I6 -- N5): set on a quiet network failure, so a waiting caller can park the pick. */
    private _validationResult: ReplaySubject<{ rejected: RejectedFile[], totalFiles: number, notice?: string, cause?: 'offline' | 'connectivity' }> = new ReplaySubject(1);
    public readonly validationResult = this._validationResult.asObservable();
    /**
     * "You picked several photos — want to make a video?" (§6.1).
     *
     * Raised from `fileSelected` right after the accept/reject split, which is where §6.1 puts it,
     * and decided from the FIRST BYTES rather than the extension: an animated GIF or WebP is a
     * clip, not a still, and nudging someone who picked two animations into a slideshow editor
     * would be a worse offer than the one they already have.
     *
     * A third subject rather than a callback because this file already publishes two and the page
     * already subscribes to one (D11) — a callback parameter would make the manager's constructor
     * the place a banner is wired, which is not where any of the other signals live.
     */
    private _stillImageNudge: ReplaySubject<StillImageNudge> = new ReplaySubject(1);
    public readonly stillImageNudge = this._stillImageNudge.asObservable();
    private _fileListChanged: ReplaySubject<{
        fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[],
        fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[]
    }> = new ReplaySubject(1);
    public readonly fileListChanged = this._fileListChanged.asObservable();

    private uploadSessionId: string;

    constructor(
        private fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[],
        private fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[],
        private props: any,
        private logger: Logger,
        private fromType: string[],
        private toType: string,
        private initializeStateUpdateFn: () => void,
        private updateSidePanelFileUploads: () => void,
        private policy?: UploadPolicy,
    ) {
        this.uploadSessionId = StringUtils.uuid();
        this.fileUploadService = container.get(FileUploadService);
        this.processorResolverService = container.get(ProcessorResolverService);
        this.uiContext = container.get(UIContext);
        this.gaService = container.get(GAService);
        this.apiConfig = container.get(ApiConfig);
        this.lightHttp = container.get(LightHttp);
        this.fileValidationService = new FileValidationService();
        this.featureRedirectService = new FeatureRedirectService(this.lightHttp, this.apiConfig);
        try { this.metricsService = container.get(MetricsService); } catch { /* optional */ }
    }

    private get quiet(): boolean {
        return this.policy?.quiet === true;
    }

    /**
     * What a quiet caller gets instead of a dialog.
     *
     * The message travels on `validationResult` because that is the channel the caller already
     * watches, and because a second channel for "something went wrong" would mean two places a
     * tray has to look. `rejected` stays empty: nothing was rejected by EXTENSION, and filling it
     * with synthetic rows would make the page's own banner claim files were the wrong type.
     */
    private reportQuietly(notice: string, cause?: 'offline' | 'connectivity') {
        // `cause` (round 6, I6 -- N5): a quiet caller that can WAIT (the Studio's tray parks the pick
        // and resumes it on `online`) needs to know the failure was the network and not the file;
        // the sentence alone is not an interface. Callers that do not read it are unchanged.
        this._validationResult.next({ rejected: [], totalFiles: 0, notice, ...(cause ? { cause } : {}) });
    }

    public updateFileList(files: UploadInProgressFile<SupportedFileTypes>[]) {
        this.fileWithMetadataList = files;
    }

    /**
     * Stop one in-flight upload by the `uuid` its `fileListChanged` row carries (R14-SH14, Studio S20 Q26).
     *
     * Contract: true when a transfer was stopped; false for an unknown uuid, an upload that already finished
     * (transfer complete or response in hand) or one already cancelled -- all no-ops. The row STAYS in
     * `fileWithMetadataList` with `state === 'cancelled'` and `fileListChanged` fires once so the caller's
     * tray re-reads; removing the row is the caller's decision, as it is for a failed one. The file's
     * `changes` emits ONE terminal event (the file, `state: 'cancelled'`) and its `whenComplete()` rejects
     * with an `UploadCancelledError` (`reason: 'cancelled'`); the page's failure paths (`setError`, the
     * error dialog, `handleError`) are not reached -- see `uploadWithRetry`. The placeholder rows
     * (`fakeUploadInProgressFileForSelectedFiles`) are not searched: nothing is in flight for them yet.
     */
    public cancel(uuid: string): boolean {
        const file = this.fileWithMetadataList.find(f => f.uuid === uuid);
        if (!file) return false;
        if (!file.cancel()) return false;
        this.notifyFileListChanged();
        return true;
    }

    public dispose() {
        this._fileListChanged.complete();
        this._validationResult.complete();
        this._stillImageNudge.complete();
    }

    private notifyFileListChanged() {
        this._fileListChanged.next({
            fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles,
            fileWithMetadataList: this.fileWithMetadataList,
        });
    }

    async fileSelected(event: FileSelectEvent) {
        // Extract files before any early-return — browser clears event.target.files after handler
        const extractedFiles = event.files ? [...event.files] : [];
        const safeEvent: FileSelectEvent = { ...event, files: extractedFiles };

        if (!ConnectivityUtils.isOnline()) {
            // Quiet effect 1 of 5 (D12).
            if (this.quiet) {
                this.reportQuietly('You are offline — files cannot be added until you are back.', 'offline');
                return;
            }
            this.uiContext.showErrorDialog({
                title: 'No Internet Connection',
                text: 'Please check your internet connection and try again.',
                confirmButtonText: 'Retry',
                successCallback: () => this.fileSelected(safeEvent),
            });
            return;
        }

        if (safeEvent && (safeEvent.target || safeEvent.files && safeEvent.files.length > 0)) {
            this.initializeStateUpdateFn();
        }
        this.fileUploadService.fileSelected();
        // Quiet effect 2 of 5 (D12). The Studio's tray shows the same fact as a row with a
        // progress bar, and a site toast on top of it would say it twice in two places.
        // Round 13 (K, R12 N2/N4): a pick FROM the frame picker is quiet here too -- its Files row carries the
        // progress bar, and the toast stacked (three deep on a second clip) over the header's Convert key, so a
        // click there hit the toast and "Convert did nothing" while another clip was preparing.
        if (!this.quiet && safeEvent.source !== 'framepicker') this.uiContext.successToast('Preparing To Upload', '');
        let dropboxFiles: any[] = filter(safeEvent.files, f => f instanceof DropboxFile);
        let driverFiles: any[] = filter(safeEvent.files, f => f instanceof GoogleDriveFile);
        let files: any[] = filter(safeEvent.files, f => f instanceof File);


        // Validate file extensions against the accept list this CALLER wants, which is the page's
        // unless a policy overrode it (§6.2, ruling 6: the Studio's list is a SUPERSET of the
        // page's). `pageData` itself is spread rather than replaced so `path` -- and therefore
        // every `pageSlug` attribute below -- stays the real route. With no policy the ORIGINAL
        // object is handed over, not a copy, so nothing about today's call changes.
        const pageData = RouteUtils.getPageDataFromProps(this.props);
        // Round 9 (L3, R8 #4): a pick FROM the frame picker admits every video xconvert takes -- the
        // picker plays what the browser cannot through the edge's `preview` rendition -- so a `.mov`
        // on the MP4 page is a file, not a four-second notice. The page's own picks are unchanged.
        const pickerExtensions = safeEvent.source === 'framepicker'
            ? everyVideoExtension(pageData?.acceptedExtensions)
            : null;
        const overrideExtensions = pickerExtensions || (this.policy?.acceptedExtensions?.length
            ? this.policy.acceptedExtensions
            : null);
        const acceptedExtensions = overrideExtensions || pageData?.acceptedExtensions;
        if (acceptedExtensions?.length > 0) {
            const allLocalFiles = [...files] as File[];
            const validation = this.fileValidationService.validate(
                allLocalFiles,
                overrideExtensions ? { ...pageData, acceptedExtensions: overrideExtensions } : pageData,
            );
            if (validation.rejected.length > 0) {
                files = validation.accepted;
                const totalFiles = allLocalFiles.length;
                // Emit metrics for each rejected file
                validation.rejected.forEach(rf => {
                    this.metricsService?.emit(MetricEventName.INVALID_FILE_SELECTED, {
                        fileName: rf.file.name,
                        fileExtension: rf.extension,
                        pageSlug: pageData.path || '',
                    });
                });
                this.metricsService?.emit(MetricEventName.VALIDATION_BANNER_SHOWN, {
                    rejectedCount: validation.rejected.length,
                    acceptedCount: validation.accepted.length,
                    pageSlug: pageData.path || '',
                });
                // Async: fetch suggestions for rejected files. totalFiles is passed through so
                // later re-emits keep saying "2 of 5" rather than collapsing to "2 of 2".
                validation.rejected.forEach(r => r.suggestionStatus = 'loading');
                this.loadSuggestionsForRejectedFiles(validation.rejected, pageData, totalFiles);
                this._validationResult.next({ rejected: validation.rejected, totalFiles });
            }
            if (files.length === 0 && dropboxFiles.length === 0 && driverFiles.length === 0) {
                return; // All files rejected, nothing to upload
            }
        }

        // FILE_SELECTED — the top of the funnel, and the sibling of the
        // INVALID_FILE_SELECTED emit above. Until now only the REJECTED half of
        // this decision was recorded, so "how many people got a usable file as
        // far as the uploader" had no denominator and CONVERT_START's 4.7k/day
        // could not be compared against anything upstream of it.
        // Emitted once per accepted file, after extension validation and before
        // the size-limit filter (a file too large for the plan was still
        // selected — that loss is measured by its absence downstream, not by
        // pretending the pick never happened).
        this.emitFileSelected(
            [...files, ...dropboxFiles, ...driverFiles],
            safeEvent.source,
            pageData?.path || '',
        );
        // §6.1: the 2+ static-images nudge sits here, after validation. Fire-and-forget and
        // deliberately not awaited -- the sniff reads at most 1 MB per file and an upload must
        // never wait on an offer.
        void this.maybeNudge(files as File[]);
        // @Deprecated
        // if (this.offlineTaskService.getOfflineMode()) {
        //     try {
        //         const capability = this.capabilityService.getConverters().find(c => c.id == RouteUtils.getPathFromProps(this.props));
        //         const canProcessOffline = capability.availability.offline;
        //         if (!canProcessOffline) {
        //             this.uiContext.showErrorDialog(
        //                 { title: 'Error', text: `Offline mode is not supported for this feature. Please turn it off and reload the page.` });
        //         }
        //     } catch (e) {
        //         console.error(e);
        //     }
        // }
        // console.log(dropboxFiles, driverFiles, files);

        const failToUploadFileNames = [];

        if (driverFiles.find(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes)) {
            failToUploadFileNames.push(driverFiles.filter(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes).map(selectedFile => selectedFile.name));
            driverFiles = driverFiles.filter(selectedFile => selectedFile.size <= this.fileUploadService.maxFileSizeInBytes);
        }

        if (dropboxFiles.find(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes)) {
            failToUploadFileNames.push(dropboxFiles.filter(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes).map(selectedFile => selectedFile.name));
            dropboxFiles = dropboxFiles.filter(selectedFile => selectedFile.size <= this.fileUploadService.maxFileSizeInBytes);
        }

        if (files.find(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes)) {
            failToUploadFileNames.push(files.filter(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes).map(selectedFile => selectedFile.name));
            files = files.filter(selectedFile => selectedFile.size <= this.fileUploadService.maxFileSizeInBytes);
        }

        if (failToUploadFileNames.length > 0) {
            this.logger.info({ message: `File Size Limit Exceeded! Failed to upload ${failToUploadFileNames.join(', ')}. Max file size limit is ${this.fileUploadService.maxFileSizeInMB} MB.` });
            // Quiet effect 3 of 5 (D12).
            if (this.quiet) {
                this.reportQuietly(`Too large for upload (limit ${this.fileUploadService.maxFileSizeInMB} MB): ${failToUploadFileNames.join(', ')}.`);
            } else {
                AlertDialog.create({
                    title: 'File Size Limit Exceeded!',
                    html: `<div style="text-align: left;">Failed to upload <ul><li>${failToUploadFileNames.join('</li><li>')}</li></ul>
              Max file size limit is ${this.fileUploadService.maxFileSizeInMB} MB.</div>`,
                    type: 'danger',
                    showCancelButton: false,
                    confirmButtonText: 'Ok!',
                });
            }
        }

        // file.size dropbox.size
        try {
            /// TODO loadFeaturePageDataForProps Note tested in Peact
            const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
            for (const file of [...dropboxFiles, ...driverFiles, ...files]) {
                const fakeUploadInProgress = await UploadInProgressFileFactory.newUploadInProgressFile(
                    file,
                    new FileUploadRequestMetadata([], file),
                    uniqueProcessType,
                );
                this.fakeUploadInProgressFileForSelectedFiles.push(fakeUploadInProgress);
                this.notifyFileListChanged();
            }


            await this.dropboxFileSelected({
                files: dropboxFiles,
                fileWithMetadataList: this.fileWithMetadataList,
                fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles
            });
            await this.googleDriveFileSelected({
                files: driverFiles,
                fileWithMetadataList: this.fileWithMetadataList,
                fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles
            });
            await this.multiFileUploadEvent({
                files: files,
                fileWithMetadataList: this.fileWithMetadataList,
                fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles
            });

            Promise.all(this.fileWithMetadataList.map(fml => fml.whenComplete())).then(
                () => {
                    this.updateSidePanelFileUploads();
                    this.notifyFileListChanged();
                },
            ).catch(console.error);

            // Stuck file watchdog — detect files at 0% with no error after 60s
            setTimeout(() => {
                this.fileWithMetadataList.forEach(f => {
                    if (!f.completed && !f.error && !f.cancelled && f.progress === 0 && ConnectivityUtils.isOnline()) {
                        this.logger.error({
                            message: 'upload.file.stuck',
                            data: {
                                uploadSessionId: this.uploadSessionId,
                                fileName: (f.file as any)?.name,
                                hasMetadata: !!f.metadata?.processorMetadataList?.length,
                            },
                        });
                    }
                });
            }, 60000);
        } catch (e) {
            this.logger.error({ error: e, message: 'File upload failed.', data: { uploadSessionId: this.uploadSessionId } });
            // Quiet effects 4 and 5 of 5 (D12): §6.2 names FOUR line ranges and calls them five,
            // and this is the block that holds two of them.
            if (this.quiet) {
                // N5: a drop of the connection between the pick and the first request surfaces here as a
                // plain fetch failure -- not a "bad file". Offline at this moment IS the cause.
                if (!ConnectivityUtils.isOnline()) {
                    this.reportQuietly('You are offline — files cannot be added until you are back.', 'offline');
                } else if (ConnectivityUtils.isConnectivityError(e)) {
                    this.reportQuietly('The upload server is temporarily unavailable — try adding the file again.', 'connectivity');
                } else {
                    this.reportQuietly('That file could not be added. Try again, or pick a different one.');
                }
            } else if (ConnectivityUtils.isConnectivityError(e)) {
                this.uiContext.showErrorDialog({
                    title: 'Server Unreachable',
                    text: 'The upload server is temporarily unavailable. Please try again.',
                    confirmButtonText: 'Retry',
                    successCallback: () => this.fileSelected(safeEvent),
                });
            } else {
                this.uiContext.handleError(e);
            }
        }
    }

    /**
     * One FILE_SELECTED per accepted file, with bounded attributes only.
     *
     * `source` is taken from the event when a call site asserted it (hand-off,
     * session tray) and otherwise derived from the file object: a DropboxFile is
     * fetched by the edge from a URL, a GoogleDriveFile through the Drive API,
     * anything else is bytes off the device.
     */
    private emitFileSelected(accepted: any[], eventSource: FileSelectSource | undefined, pageSlug: string) {
        for (const file of accepted) {
            try {
                const source: FileSelectSource = eventSource
                    || (file instanceof GoogleDriveFile ? 'drive'
                        : file instanceof DropboxFile ? 'url'
                            : 'local');
                this.metricsService?.emit(MetricEventName.FILE_SELECTED, {
                    pageSlug,
                    fileExtension: safeExt(file?.name || ''),
                    sizeBucket: sizeBucket(file?.size),
                    source,
                });
            } catch { /* telemetry is never load-bearing */ }
        }
    }

    /**
     * Raise the nudge when every accepted local file is a STILL image and there are at least two.
     *
     * Decided from the bytes (P5a's `isAnimatedImage`, which is ironman's four sniffers ported
     * exactly), never from the extension: `.gif` and `.webp` are both, and an animated one is a
     * clip. Anything that cannot be read is treated as "not obviously a still" and the nudge stays
     * quiet -- an offer made on a guess is worse than no offer.
     *
     * `files` is the ACCEPTED local set: a Dropbox or Drive pick has no bytes on this device to
     * sniff, and a file validation rejected is not one the user gets to put in a video either.
     *
     * EVERY CHEAP TEST COMES BEFORE THE READ. `isAnimatedImage` answers `false` with no I/O for
     * jpg, tiff and the rest, but for png/apng/webp/gif/avif/heic/heif it slices and reads
     * `ANIMATED_SNIFF_BYTES` -- 1 MB per file, sequentially, alongside the upload the user is
     * waiting on. On `compress-png`, `heic-to-jpg`, `webp-to-jpg` and every other page the Studio
     * does not light, that read buys nothing: the page throws the emission away. A 50-file HEIC
     * pick off a phone is ~50 MB of pointless reads. So the page question is asked FIRST, from the
     * leaf predicate the tab strip and the banner also use.
     */
    /**
     * May this pick be offered to the Studio at all — asked BEFORE any byte is read.
     *
     * A thunk that throws answers NO. It is a caller's predicate running inside the upload path,
     * and an offer is never load-bearing: failing closed costs a banner, failing open would put a
     * stranger's exception between a user and their file.
     */
    private nudgeAllowed(): boolean {
        const gate = this.policy?.nudge;
        if (gate === undefined || gate === null) return true;
        if (typeof gate === 'function') {
            try {
                return gate() === true;
            } catch {
                return false;
            }
        }
        return gate !== false;
    }

    private async maybeNudge(files: File[]) {
        try {
            if (!this.nudgeAllowed()) return;
            if (!files || files.length < 2) return;
            if (!pageAcceptsStudio(RouteUtils.getPageDataFromProps(this.props))) return;
            for (const file of files) {
                const ext = (file.name.split('.').pop() || '').toLowerCase();
                if (!ext || ext === file.name.toLowerCase()) return;
                if (await isAnimatedImage(file, ext)) return;
            }
            this._stillImageNudge.next({ files: files.length });
        } catch { /* an offer is never load-bearing */ }
    }

    async dropboxFileSelected(request: FileSelectHandleRequest<DropboxFile>): Promise<any> {
        if (request.files.length > 0) {
            const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
            const currentRequestMetadata: UploadInProgressFile<DropboxFile>[] = [];
            for (const file of request.files) {
                currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                    file, null, uniqueProcessType,
                ));
            }
            request.fileWithMetadataList.push(...currentRequestMetadata);
            this.notifyFileListChanged();

            try {
                for (const currentRequest of currentRequestMetadata) {
                    this.gaService.send(
                        GAContants.HitType.EVENT, GAContants.EventCategory['SELECT-DIALOG'],
                        GAContants.EventAction['FILE-SELECTED'], GAContants.EventLabel.SELECT,
                        `drive-${currentRequest.file.name}-${currentRequest.file.size}`,
                    );
                    try {
                        const metadata = await this.resolveWithRetry(currentRequest.file);
                        currentRequest.setMetadata(metadata);
                    } catch (e) {
                        this.logger.error({ message: `Resolve failed for ${currentRequest.file.name}`, error: e, data: { uploadSessionId: this.uploadSessionId } });
                        currentRequest.setError(() => this.retryResolveAndUpload(
                            currentRequest, (fwm, obs, opts) => this.fileUploadService.uploadFileFromURL(fwm, obs, opts)));
                    }
                }
                this.notifyFileListChanged();

                const resolvedFiles = currentRequestMetadata.filter(f => f.metadata?.processorMetadataList?.length > 0);
                const promiseArray = [];
                for (const fileWithMetadata of resolvedFiles) {
                    promiseArray.push(new Promise<void>((resolve, reject) => {
                        const observerObject = {
                            next: x => { fileWithMetadata.setProgress(x); },
                            error: err => {
                                this.logger.error({ message: `Error uploading file (Dropbox).`, error: err, data: { uploadSessionId: this.uploadSessionId } });
                                fileWithMetadata.setError();
                            },
                            complete: () => { fileWithMetadata.setComplete(); },
                        };
                        this.uploadWithRetry(
                            fileWithMetadata,
                            (fwm, obs, opts) => this.fileUploadService.uploadFileFromURL(fwm, obs, opts),
                            observerObject,
                        ).then(() => resolve()).catch((e) => reject(e));
                    }));
                }
                await Promise.all(promiseArray);
            } finally {
                request.files.forEach(f => {
                    const idx = request.fakeUploadInProgressFileForSelectedFiles.findIndex(fakeF => fakeF.file === f);
                    if (idx > -1) {
                        request.fakeUploadInProgressFileForSelectedFiles.splice(idx, 1);
                    }
                });
                this.notifyFileListChanged();
            }
        }
    }

    async googleDriveFileSelected(request: FileSelectHandleRequest<GoogleDriveFile>) {
        if (request.files.length > 0) {
            const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
            this.logger.trace7({ message: 'Goolge drive file selected.', data: request.files });
            const currentRequestMetadata: UploadInProgressFile<any>[] = [];

            for (const file of request.files) {
                currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                    file, null, uniqueProcessType,
                ));
            }
            request.fileWithMetadataList.push(...currentRequestMetadata);
            this.notifyFileListChanged();

            try {
                for (const currentRequest of currentRequestMetadata) {
                    this.gaService.send(
                        GAContants.HitType.EVENT, GAContants.EventCategory['SELECT-DIALOG'],
                        GAContants.EventAction['FILE-SELECTED'], GAContants.EventLabel.SELECT,
                        `drive-${currentRequest.file.name}-${currentRequest.file.size}`,
                    );
                    try {
                        const metadata = await this.resolveWithRetry(currentRequest.file);
                        currentRequest.setMetadata(metadata);
                    } catch (e) {
                        this.logger.error({ message: `Resolve failed for ${currentRequest.file.name}`, error: e, data: { uploadSessionId: this.uploadSessionId } });
                        currentRequest.setError(() => this.retryResolveAndUpload(
                            currentRequest, (fwm, obs, opts) => this.fileUploadService.uploadFileFromGoogleDrive(fwm as UploadInProgressFile<File>, obs, opts)));
                    }
                }
                this.notifyFileListChanged();

                const resolvedFiles = currentRequestMetadata.filter(f => f.metadata?.processorMetadataList?.length > 0);
                const promiseArray = [];
                for (const fileWithMetadata of resolvedFiles) {
                    promiseArray.push(new Promise<void>((resolve, reject) => {
                        const observerObject = {
                            next: x => { fileWithMetadata.setProgress(x); },
                            error: err => {
                                this.logger.error({ message: `Error uploading file (GoogleDrive).`, error: err, data: { uploadSessionId: this.uploadSessionId } });
                                fileWithMetadata.setError();
                            },
                            complete: () => { fileWithMetadata.setComplete(); },
                        };
                        this.uploadWithRetry(
                            fileWithMetadata,
                            (fwm, obs, opts) => this.fileUploadService.uploadFileFromGoogleDrive(fwm as UploadInProgressFile<File>, obs, opts),
                            observerObject,
                        ).then(() => resolve()).catch((e) => reject(e));
                    }));
                }
                await Promise.all(promiseArray);
            } finally {
                request.files.forEach(f => {
                    const idx = request.fakeUploadInProgressFileForSelectedFiles.findIndex(fakeF => fakeF.file === f);
                    if (idx > -1) {
                        request.fakeUploadInProgressFileForSelectedFiles.splice(idx, 1);
                    }
                });
                this.notifyFileListChanged();
            }
        }
    }

    async multiFileUploadEvent(request: FileSelectHandleRequest<File>) {
        const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;

        const currentRequestMetadata: UploadInProgressFile<any>[] = [];
        for (const file of request.files) {
            currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                file,
                null,
                uniqueProcessType,
            ));
        }
        request.fileWithMetadataList.push(...currentRequestMetadata);
        this.notifyFileListChanged();

        try {
            for (const currentRequest of currentRequestMetadata) {
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory['SELECT-DIALOG'],
                    GAContants.EventAction['FILE-SELECTED'],
                    GAContants.EventLabel.SELECT,
                    `file-${currentRequest.file.name}-${currentRequest.file.size}`,
                );
                try {
                    const metadata = await this.resolveWithRetry(currentRequest.file);
                    currentRequest.setMetadata(metadata);
                } catch (e) {
                    this.logger.error({ message: `Resolve failed for ${currentRequest.file.name}`, error: e, data: { uploadSessionId: this.uploadSessionId } });
                    currentRequest.setError(() => this.retryResolveAndUpload(
                        currentRequest, (fwm, obs, opts) => this.fileUploadService.uploadFileFromLocalDisk(fwm as UploadInProgressFile<File>, obs, opts)));
                }
            }

            const resolvedFiles = currentRequestMetadata.filter(f => f.metadata?.processorMetadataList?.length > 0);
            const CONCURRENT_LIMIT = 4;
            for (let i = 0; i < resolvedFiles.length; i += CONCURRENT_LIMIT) {
                const batch = resolvedFiles.slice(i, i + CONCURRENT_LIMIT);
                await Promise.all(batch.map(fileWithMetadata => new Promise<void>((resolve, reject) => {
                    const observerObject = {
                        next: x => { fileWithMetadata.setProgress(x); },
                        error: err => {
                            this.logger.error({ message: `Error uploading file (Disk_).`, error: err, data: { uploadSessionId: this.uploadSessionId } });
                            fileWithMetadata.setError();
                        },
                        complete: () => { fileWithMetadata.setComplete(); },
                    };
                    this.uploadWithRetry(
                        fileWithMetadata,
                        (fwm, obs, opts) => this.fileUploadService.uploadFileFromLocalDisk(fwm as UploadInProgressFile<File>, obs, opts),
                        observerObject,
                    ).then(() => resolve()).catch((e) => reject(e));
                })));
            }
        } finally {
            request.files.forEach(f => {
                const idx = request.fakeUploadInProgressFileForSelectedFiles.findIndex(fakeF => fakeF.file === f);
                if (idx > -1) {
                    request.fakeUploadInProgressFileForSelectedFiles.splice(idx, 1);
                }
            });
            this.notifyFileListChanged();
        }
    }

    private async resolveWithRetry(file: any, maxRetries = 1): Promise<FileUploadRequestMetadata> {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                return await this.processorResolverService.generateFileUploadRequestMetadata(
                    file, this.fromType, this.toType, OperationTypes.UPLOAD,
                    attempt > 0 ? { suppressGA: true } : undefined);
            } catch (e) {
                // Enforcement / billing rejections (429 enforcement_error,
                // 402 insufficient_credits) have already surfaced via the
                // global EnforcementErrorModal inside the resolver. Re-trying
                // would just retrigger the same rejection — bail out.
                if (EnforcementErrorService.isEnforcementError(e) || attempt === maxRetries) throw e;
                this.logger.warn({ message: `Resolve failed (attempt ${attempt + 1}), retrying.`, error: e, data: { uploadSessionId: this.uploadSessionId } });
            }
        }
        throw new Error('Resolve failed');
    }

    private async retryResolveAndUpload(
        fileWithMetadata: UploadInProgressFile<SupportedFileTypes>,
        uploadFn: (fwm: UploadInProgressFile<SupportedFileTypes>, obs: Observer<any>, opts?: UploadOptions) => Promise<OriginalInputFile>,
    ): Promise<void> {
        fileWithMetadata.error = false;
        fileWithMetadata.setProgress(0);
        const metadata = await this.processorResolverService.generateFileUploadRequestMetadata(
            fileWithMetadata.file as any, this.fromType, this.toType, OperationTypes.UPLOAD);
        fileWithMetadata.setMetadata(metadata);
        const observerObject = {
            next: x => { fileWithMetadata.setProgress(x); },
            error: err => { fileWithMetadata.setError(); },
            complete: () => { fileWithMetadata.setComplete(); },
        };
        await this.uploadWithRetry(fileWithMetadata, uploadFn, observerObject);
    }


    private async uploadWithRetry(
        fileWithMetadata: UploadInProgressFile<SupportedFileTypes>,
        uploadFn: (fwm: UploadInProgressFile<SupportedFileTypes>, obs: Observer<any>, opts?: UploadOptions) => Promise<OriginalInputFile>,
        baseObserverObject: Observer<any>,
        maxRetries = 2,
    ): Promise<void> {
        const excludeDomains: string[] = [];
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            // R14-SH14: a cancel between attempts (during the re-resolve) must not start a new Resumable.
            if (fileWithMetadata.cancelled) return;
            const isFinalAttempt = attempt === maxRetries;
            const isFirstAttempt = attempt === 0;
            const observerObject = {
                ...baseObserverObject,
                error: (err) => { if (isFinalAttempt) baseObserverObject.error(err); },
            };
            try {
                const result = await uploadFn(fileWithMetadata, observerObject, {
                    suppressErrorDialog: !isFinalAttempt,
                    suppressGAStart: !isFirstAttempt,
                    suppressGAFailed: !isFinalAttempt,
                    // Surfaced as FILE_UPLOAD_*.retries so the edge-reselection
                    // loop stops being invisible.
                    attempt,
                });
                fileWithMetadata.setResponse(result);
                return;
            } catch (e) {
                // R14-SH14: the user stopped it. Not a failure: no `setError`, no retry closure, and no throw
                // into `fileSelected`'s catch (which would raise the page's error dialog or quiet notice).
                if (isUploadCancelled(e) || fileWithMetadata.cancelled) return;
                if (!ConnectivityUtils.isRetryableUploadError(e) || isFinalAttempt) {
                    fileWithMetadata.setError(() => this.uploadWithRetry(
                        fileWithMetadata, uploadFn, baseObserverObject, maxRetries));
                    throw e;
                }
                const failedDomain = fileWithMetadata.metadata?.processorMetadataList?.[0]?.uploadDomain;
                if (failedDomain) excludeDomains.push(failedDomain);
                this.logger.warn({ message: `Upload failed (attempt ${attempt + 1}), re-resolving edge. Excluding: ${excludeDomains.join(', ')}`, error: e, data: { uploadSessionId: this.uploadSessionId } });
                fileWithMetadata.error = false;
                fileWithMetadata.setProgress(0);
                const newMetadata = await this.processorResolverService
                    .generateFileUploadRequestMetadata(
                        fileWithMetadata.file as any, this.fromType, this.toType,
                        OperationTypes.UPLOAD, { suppressGA: true, excludeDomains });
                fileWithMetadata.setMetadata(newMetadata);
            }
        }
    }

    /**
     * @param totalFiles the size of the batch the user actually selected. It is threaded through
     * rather than recomputed, because `rejected.length` is a different number: re-emitting with
     * it made the headline mutate from "2 of 5 files were not uploaded" to "2 of 2" the moment
     * suggestions landed, which tells the user their other three files vanished too.
     */
    private async loadSuggestionsForRejectedFiles(rejected: RejectedFile[], pageData: any, totalFiles: number) {
        const uniqueExts = [...new Set(rejected.map(r => r.extension))];
        for (const ext of uniqueExts) {
            const forExt = rejected.filter(r => r.extension === ext);
            try {
                const suggestions = await this.featureRedirectService.findRelevantPages(ext, pageData);
                forExt.forEach(r => {
                    r.suggestedPages = suggestions;
                    r.suggestionStatus = 'loaded';
                });
            } catch {
                // Best-effort, but the failure has to be visible: an unmarked failure leaves the
                // row saying "Finding the right tool..." for the rest of the session.
                forExt.forEach(r => r.suggestionStatus = 'failed');
            }
            // Emit a new array reference so React detects the change
            this._validationResult.next({ rejected: [...rejected], totalFiles });
        }
    }
}
